import React, { useEffect } from "react";
import { Bar, Line } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend
);

// Radar chart removed for Phase 1 — its data (per-category average seconds)
// doesn't exist yet because no question categories are being played. It
// belongs back here once Phase 2's game engine is logging per-category timing.
//
// Bar + Line now read from `liveFeed` (polled every 15s by useReunionAdmin)
// instead of the event prop's static totals, so refreshing shows real motion.

const ReunionLiveFeed = ({ event, liveFeed, startPolling, stopPolling }) => {
  useEffect(() => {
    startPolling();
    return () => stopPolling();
    // eslint-disable-next-line
  }, []);

  const data = liveFeed || event;
  const stats = data.liveStats || [];

  const barData = {
    labels: ["Answers", "Correct", "Wrong"],
    datasets: [
      {
        label: "Live",
        data: [
          data.answersSubmitted,
          data.correctAnswers,
          data.answersSubmitted - data.correctAnswers,
        ],
      },
    ],
  };

  const lineData = {
    labels: stats.map((point) =>
      new Date(point.timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    ),
    datasets: [
      {
        label: "Answers Submitted (per snapshot)",
        data: stats.map((point) => point.answersSubmitted),
      },
    ],
  };

  return (
    <div className="space-y-3">
      <div
        className={`rounded-3xl p-6 border ${
          data.isLive
            ? "bg-green-500/10 border-green-500/30"
            : "bg-yellow-500/10 border-yellow-500/30"
        }`}
      >
        <div className="flex justify-between items-center flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-black text-cyan-200">{data.hallName}</h2>
            <p className="text-emerald-200">{data.specialMessage}</p>
          </div>
          <div className="text-2xl font-black">
            {data.isLive ? "🟢 LIVE" : "🟡 WAITING"}
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid lg:grid-cols-4 gap-5">
        <Card emoji="👥" title="Participants" value={data.participantsJoined} />
        <Card emoji="✍️" title="Answers" value={data.answersSubmitted} />
        <Card emoji="✅" title="Correct" value={data.correctAnswers} />
        <Card
          emoji="❌"
          title="Wrong"
          value={data.answersSubmitted - data.correctAnswers}
        />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-5">
        <ChartCard title="📊 Answer Summary">
          <Bar data={barData} />
        </ChartCard>

        <ChartCard title="📈 Live Trend">
          {stats.length > 0 ? (
            <Line data={lineData} />
          ) : (
            <div className="text-center text-emerald-300 text-sm py-10">
              Trend will appear once the event goes live and answers start
              coming in.
            </div>
          )}
        </ChartCard>
      </div>
    </div>
  );
};

const Card = ({ emoji, title, value }) => (
  <div className="rounded-2xl bg-white/5 border border-white/10 p-5 text-center">
    <div className="text-4xl">{emoji}</div>
    <div className="text-sm text-emerald-300 mt-2">{title}</div>
    <div className="text-3xl font-black mt-2">{value}</div>
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className="rounded-3xl bg-white/5 border border-white/10 p-6">
    <h3 className="text-xl font-bold mb-5 text-cyan-200">{title}</h3>
    {children}
  </div>
);

export default ReunionLiveFeed;
